import { cn } from '@/lib/cn';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import type { ReactNode } from 'react';

const cardVariants = cva(
  'flex flex-col gap-2 rounded-lg p-4 text-card-foreground',
  {
    variants: {
      variant: {
        default: 'border bg-card',
        muted: 'bg-subtle/80',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

interface CardProps extends React.ComponentProps<'div'> {
  initial?: boolean;
  variant?: 'default' | 'muted';
}

function Card({
  className,
  initial = true,
  variant = 'default',
  ref,
  ...props
}: CardProps) {
  return (
    <div
      ref={ref}
      className={cn(
        cardVariants({ variant }),
        { 'animate-in fade-in slide-in-from-bottom-[10px]': initial },
        className
      )}
      {...props}
    />
  );
}

type CardHeaderProps = React.ComponentProps<'div'>;

function CardHeader({ className, ref, ...props }: CardHeaderProps) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex flex-col rounded-t-lg border-b bg-transparent pb-3',
        className
      )}
      {...props}
    />
  );
}

type CardTitleProps = React.ComponentProps<'h3'>;

function CardTitle({ className, children, ref, ...props }: CardTitleProps) {
  return (
    <div className='flex items-center'>
      <h3
        ref={ref}
        className={cn('text-base leading-none font-medium', className)}
        {...props}
      >
        {children}
      </h3>
    </div>
  );
}

type CardDescriptionProps = React.ComponentProps<'p'>;

function CardDescription({ className, ref, ...props }: CardDescriptionProps) {
  return (
    <p
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

type CardContentProps = React.ComponentProps<'div'>;

function CardContent({ className, ref, ...props }: CardContentProps) {
  return <div ref={ref} className={cn(className)} {...props} />;
}

type CardFooterProps = React.ComponentProps<'div'>;

function CardFooter({ className, ref, ...props }: CardFooterProps) {
  return (
    <div ref={ref} className={cn('flex items-center', className)} {...props} />
  );
}

type CardContentRowProps = React.ComponentProps<'div'>;

function CardContentRow({ className, ref, ...props }: CardContentRowProps) {
  return (
    <div
      ref={ref}
      className={cn(
        'flex w-full justify-between gap-2 py-2 first:pt-2',
        className
      )}
      {...props}
    />
  );
}

type CardContentRowTitleProps = React.ComponentProps<'span'>;

function CardContentRowTitle({
  className,
  ref,
  ...props
}: CardContentRowTitleProps) {
  return (
    <span
      ref={ref}
      className={cn(
        'text-sm leading-snug font-normal text-muted-foreground',
        className
      )}
      {...props}
    />
  );
}

interface CardInfoRowProps {
  title: string;
  value?: string | ReactNode | undefined | null;
}

function CardInfoRow({ title, value }: CardInfoRowProps) {
  const isString = typeof value === 'string';
  return (
    <div>
      <div className='text-sm text-muted-foreground'>{title}</div>
      {!value || isString ? (
        <div className='text-sm font-medium'>{value ?? '-'}</div>
      ) : (
        <div>{value}</div>
      )}
    </div>
  );
}

export {
  Card,
  CardContent,
  CardContentRow,
  CardContentRowTitle,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  CardInfoRow,
};
